import { INTL_DMY_CASUAL_FORMAT, INTL_SHORT_TIME_FORMAT_AM_PM } from 'constants/dateFormats'
import {
  customPriceBreakdownTitle,
  knownPriceBreakdownTitle,
} from 'checkout/constants/priceBreakdownTitles'
import { nonNullable, sum } from 'lib/array/arrayUtils'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import getLoyaltyProductType from 'loyalty/lib/getLoyaltyProductType'
import { makeDateTime } from 'tripPlanner/utils'
import type { IntlShape } from 'react-intl'

function getCarHireInsuranceItem(intl: IntlShape, carHireInsurance: App.CarHireInsurance): App.Checkout.AnyItemBreakdownView {
  const insuranceTitle = intl.formatMessage({
    id: 'GdIdC64bhV_yPvb7YMVz',
    defaultMessage: 'Insurance',
  })

  if (!carHireInsurance || carHireInsurance?.type === 'limited') {
    return {
      title: insuranceTitle,
      itemType: 'car_hire',
      additionalInfo: [],
      additionalElements: [{
        id: 'limited_protection',
        left: {
          type: 'text',
          value: intl.formatMessage({
            id: 'iZ1hPyYGEKCxsxikDnYt',
            defaultMessage: 'Limited Protection',
          }),
        },
        right: {
          type: 'price',
          value: 0,
        },
      }],
    }
  }

  return {
    title: insuranceTitle,
    itemType: 'car_hire',
    additionalInfo: [],
    additionalElements: [{
      id: carHireInsurance.id,
      left: {
        type: 'text',
        value: intl.formatMessage({
          id: 'UE1y9G-R6NbO1qyqOJGs',
          defaultMessage: 'Enhanced Protection',
        }),
      },
      right: {
        type: 'price',
        value: 0,
      },
    }],
  }
}

function getCarHireAddonsItems(intl: IntlShape, selectedAddons: Record<string, App.CartCarHireAddon>, taxesAndFees?: number): App.Checkout.AnyItemBreakdownView {
  return {
    title: intl.formatMessage({
      id: 'q9ZmFQbyeG9z8cIGUvti',
      defaultMessage: 'Add-ons',
    }),
    itemType: 'car_hire',
    additionalInfo: [],
    taxesAndFees,
    additionalElements: Object.values(selectedAddons).map(addon => {
      return {
        id: addon.description || '',
        left: {
          type: 'text',
          value: addon.description || '',
          quantity: addon.quantity || 1,
        },
        right: {
          type: 'price',
          value: addon.total * (addon.quantity || 1),
        },
      }
    }),
  }
}

export function getSelectedCarHireAddonsTotal(selectedAddons: Array<Record<string, App.CartCarHireAddon>>) {
  return sum(selectedAddons, addon => {
    return sum(Object.values(addon), addon => addon.total * (addon.quantity || 1))
  })
}

export function getCarHireSelectedRateOption(
  item: App.Checkout.CarHireItem,
): App.CarHireOffer['rateOptions'][number] {
  return item.offer.rateOptions.find((option) =>
    option.id === item.selectedRateOption.id &&
    option.idContext === item.selectedRateOption.idContext,
  ) ?? item.offer.rateOptions[0]
}

export function getCarHireItemView(
  intl: IntlShape,
  item: App.Checkout.CarHireItem,
): App.Checkout.CarHireItemView {
  const rateOption = getCarHireSelectedRateOption(item)
  const isPayOnArrival = !rateOption.payNowAmount && !!rateOption.payOnArrivalAmount
  const orSimilarSuffix = item.offer.guarenteedModel ? '' : intl.formatMessage({
    id: 'LyUQounSfH1mnKVVSA2U',
    defaultMessage: 'or similar',
  })

  return {
    title: `${item.offer.vehicle.model} ${orSimilarSuffix}`,
    loyalty: {
      productType: getLoyaltyProductType(item.offer),
    },
    kind: 'car-hire',
    item,
    totals: {
      taxesAndFees: !isPayOnArrival ? rateOption.payOnArrivalAmount : 0,
      price: item.price,
      value: item.value,
      memberPrice: 0,
      memberValue: 0,
      surcharge: 0,
      extraGuestSurcharge: 0,
      carHirePayOnArrival: isPayOnArrival ? rateOption.payOnArrivalAmount : 0,
    },
    model: item.offer.vehicle.model,
    image: item.offer.vehicle.image,
    pickUpLocation: item.offer.pickUp,
    returnLocation: item.offer.dropOff,
    addons: item.offer.addons,
    vendor: {
      name: item.offer.vendor.name,
      image: {
        url: `https://ctimg-svg.cartrawler.com/supplier-images/${item.offer.vendor.name.toLowerCase().replaceAll(' ', '_')}.svg`,
        title: item.offer.vendor.name,
      },
    },
  }
}

function getCarHireCancellationPolicyItem(
  offerCancellationPolicy: App.CarHireCancellationPolicy,
): App.Checkout.AnyItemBreakdownView {
  return {
    cancellationPolicy: {
      maxHours: offerCancellationPolicy.maxHours,
      maxDate: offerCancellationPolicy.maxDate,
    },
    title: '',
    additionalInfo: [],
    additionalElements: [],
    itemType: 'car_hire',
  }
}

function getCarHireDiscountItem(
  intl: IntlShape,
  carView: App.Checkout.CarHireItemView,
): App.Checkout.CarHireItemBreakdownView {
  const rateOption = getCarHireSelectedRateOption(carView.item)

  const discount = rateOption.payNowDiscountAmount ?? 0
  const discountPercentage = rateOption.payNowDiscountPercentage ?? 0

  if (discount <= 0) {
    return {
      title: '',
      itemType: 'car_hire',
      additionalInfo: [],
      additionalElements: [],
    }
  }

  return {
    title: '',
    itemType: 'car_hire',
    additionalInfo: [],
    additionalElements: [{
      id: 'discount',
      left: {
        type: 'text',
        value: intl.formatMessage(
          { id: 'rd1B2CfhxfRdWFNsSU63', defaultMessage: '{discountPercentage}% off' },
          { discountPercentage },
        ),
      },
      right: {
        type: 'price',
        value: -discount,
      },
    }],
  }
}

export function getCarHireInsurancePriceBreakdown(
  intl: IntlShape,
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  return {
    title: knownPriceBreakdownTitle('carHireInsurance'),
    price: carView.item.selectedInsurance?.total || 0,
    memberPrice: 0,
    additionalInfoText: [],
    items: [
      ...carView.item.selectedInsurance ? [getCarHireInsuranceItem(intl, carView.item.selectedInsurance)] : [],
    ],
    itemsAlwaysVisible: true,
    alwaysShowItemPrice: true,
  }
}

export function getCarHireAddonPriceBreakdown(
  intl: IntlShape,
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  const selectedCarHireAddonTotal = getSelectedCarHireAddonsTotal([carView.item.selectedAddons])
  return {
    title: knownPriceBreakdownTitle('carHireAddons'),
    price: selectedCarHireAddonTotal,
    memberPrice: 0,
    additionalInfoText: [],
    items: selectedCarHireAddonTotal ? [getCarHireAddonsItems(intl, carView.item.selectedAddons, carView.totals.taxesAndFees)] : [],
  }
}

export function getCarHireItemPriceBreakdown(
  intl: IntlShape,
  carView: App.Checkout.CarHireItemView,
): App.Checkout.PriceBreakdownView {
  const { offer } = carView.item
  const isSameLocation = offer.pickUp.name === offer.dropOff.name
  const orSimilarSuffix = carView.item.offer.guarenteedModel ? '' : intl.formatMessage({
    id: 'LyUQounSfH1mnKVVSA2U',
    defaultMessage: 'or similar',
  })
  const description = isSameLocation
    ? intl.formatMessage(
        { id: 'aVrae1dbrlVnDx6BtPIp', defaultMessage: 'Pick up and drop-off at {locationName}' },
        { locationName: offer.pickUp.name },
      )
    : intl.formatMessage(
        { id: 'KUwGV_Un7y6NHVaJbBRE', defaultMessage: 'Pick up at {pickUpName} and drop-off at {dropOffName}' },
        { pickUpName: offer.pickUp.name, dropOffName: offer.dropOff.name },
      )

  const pickUpDateTime = makeDateTime(carView.item.pickUpDate, carView.item.pickUpTime)
  const dropOffDateTime = makeDateTime(carView.item.dropOffDate, carView.item.dropOffTime)
  const pickUpLabel = `${intlDateFormatter(pickUpDateTime, INTL_DMY_CASUAL_FORMAT, intl.locale)}, ${intlDateFormatter(pickUpDateTime, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)}`
  const dropOffLabel = `${intlDateFormatter(dropOffDateTime, INTL_DMY_CASUAL_FORMAT, intl.locale)}, ${intlDateFormatter(dropOffDateTime, INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)}`

  return {
    title: customPriceBreakdownTitle(`${carView.model} ${orSimilarSuffix}`),
    price: carView.totals.value,
    memberPrice: 0,
    description,
    additionalInfoText: [
      `${pickUpLabel} → ${dropOffLabel}`,
      intl.formatMessage({
        id: 'ehNXQilkzzFHCNp27hf3',
        defaultMessage: '{count, plural, one {# day} other {# days}}',
      }, { count: offer.duration }),
    ],
    items: nonNullable([
      getCarHireDiscountItem(intl, carView),
      offer.cancellationPolicy ? getCarHireCancellationPolicyItem(offer.cancellationPolicy) : undefined,
    ]),
    itemsAlwaysVisible: true,
    alwaysShowItemPrice: true,
  }
}
